import type { BoardResBoardType } from '@/api/gen/_model/boardResBoardType';
import { type BoardTranslationKeys } from '@/types/translation';

/**
 * 보드 구조. 서버 `BoardType`(= `Board.toBoardType()`)과 템플릿 `type` 이 같은 값을 쓴다.
 *
 * **손으로 쓰지 않고 생성 타입에서 파생한다.** 예전엔 같은 값 집합이 여기와
 * `BoardDetailData` 에 각각 손선언돼 있어, 서버 enum 이 늘 때 한쪽만 고쳐도 타입 에러가
 * 안 났다 — 아래 Record 의 누락 감지가 반쪽이 되는 셈이다. 이제 orval 재생성이 곧
 * 유니온 확장이고, 라벨 누락은 컴파일 단계에서 잡힌다.
 */
export type BoardTypeValue = BoardResBoardType;

const BOARD_TYPE_LABEL_KEY: Record<BoardTypeValue, BoardTranslationKeys> = {
  WALL: 'myBoard.card.type.wall',
  GROUP: 'board.group.button',
  TIMELINE: 'board.timeline.type',
};

/**
 * 보드 유형 라벨 — my-board 카드 / 학급 콘텐츠 chip / /board/new 템플릿 뱃지가 공유한다.
 *
 * 한 곳으로 모은 이유: 세 자리가 각자 분기를 들고 있다가 값이 늘 때마다 같은 버그를
 * 반복했다. GROUP 이 생겼을 때 학급 chip 이(고정 문자열이라) 담벼락으로 남았고,
 * TIMELINE 이 생겼을 때 my-board 삼항이 또 담벼락으로 떨어졌다. 이제 값이 늘면
 * `BOARD_TYPE_LABEL_KEY` 의 Record 가 누락을 타입 에러로 잡는다.
 *
 * 라벨은 전부 `board` 네임스페이스 키를 쓴다 — 학급 chip 이 쓰던 classroom 쪽
 * `boardChip` 키는 ja 번역이 「壁」로 갈라져 있어(board 쪽은 「ウォール」) 같은 개념이
 * 두 표기로 노출됐다. 호출부는 `useTranslation` 의 **기본 ns 가 `board`** 여야 한다
 * (= 배열의 첫 항목). 그래야 prefix 없는 키가 그대로 해석된다.
 *
 * boardType 이 없으면(구 응답·비보드 링크) 담벼락으로 떨어뜨린다 — 기존 동작 유지.
 *
 * **Record 조회 결과도 같은 규칙으로 떨어뜨린다.** 위 Record 가 잡는 누락은 컴파일
 * 시점의 것뿐이라, 서버가 FE 보다 먼저 배포돼 아직 모르는 값이 오면(BE→FE 배포 순서를
 * 쓰는 한 그 창은 매 배포마다 열린다) 타입은 통과하고 런타임에 `undefined` 가 `t()` 로
 * 넘어가 라벨 세 자리가 동시에 빈칸이 된다. 값을 모르는 것과 값이 없는 것은 사용자에게
 * 같은 상황이므로 폴백도 같아야 한다.
 */
export function getBoardTypeLabel(
  t: (key: BoardTranslationKeys) => string,
  boardType: BoardTypeValue | undefined,
): string {
  return t(
    (boardType && BOARD_TYPE_LABEL_KEY[boardType]) ?? BOARD_TYPE_LABEL_KEY.WALL,
  );
}
